import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { redactSecrets } from "@/lib/redact-secrets";
import { QueryProvider } from "@/providers/query-provider";
import { routeTree } from "./routeTree.gen";

Sentry.init({
  dsn: "https://58a1e0d3394f5564700e969061521879@o4510951567917056.ingest.de.sentry.io/4510952838463568",
  enableLogs: true,
  // TODO: see about replacing all `console.` calls with `logger.` so that we ca remove this
  integrations: [
    // send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  // CR-111: `enableLogs` + consoleLoggingIntegration forwards every console.*
  // call to Sentry, so a Kairo API key logged anywhere — by us or by a
  // dependency echoing a request — would leave the browser as a live bearer
  // credential. Scrub key-shaped strings out of both channels. Nothing logs one
  // today; this is what keeps that true.
  beforeSend: (event) => redactSecrets(event),
  beforeSendLog: (log) => redactSecrets(log),
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.DEV ? "dev" : undefined,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute - prevent excessive refetching
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false, // Disable automatic refetch on focus
      refetchOnReconnect: true, // Refetch when connection is restored
      refetchOnMount: true, // Refetch when component mounts
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
});

export type AppRouter = typeof router;

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryProvider>
    </StrictMode>,
  );
}
